import authAPI from './authAPI';

class GoogleAuthService {
  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    this.redirectUri = `${window.location.origin}/auth/google/callback`;
    this.isInitialized = false;
  }

  // Initialize Google OAuth
  async initialize() {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      // Load Google Identity Services script
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.isInitialized = true;
          resolve();
        };
        script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
        document.head.appendChild(script);
      } else {
        this.isInitialized = true;
        resolve();
      }
    });
  }

  // Get Google OAuth authorization URL
  getAuthUrl() {
    if (!this.clientId) {
      throw new Error('Google OAuth not configured');
    }

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: 'openid email profile',
      response_type: 'code',
      access_type: 'offline',
      prompt: 'consent'
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  // Redirect to Google OAuth
  redirectToGoogle() {
    window.location.href = this.getAuthUrl();
  }

  // Handle OAuth callback (extract code from URL)
  handleCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      throw new Error(`Google OAuth error: ${error}`);
    }

    if (!code) {
      throw new Error('No authorization code received from Google');
    }

    return code;
  }

  // Exchange authorization code for JWT tokens
  async exchangeCodeForTokens(code) {
    try {
      const response = await authAPI.googleLogin(code, this.redirectUri);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Complete Google OAuth flow
  async completeOAuthFlow() {
    try {
      const code = this.handleCallback();
      const authData = await this.exchangeCodeForTokens(code);
      
      // Clear the URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
      
      return authData;
    } catch (error) {
      throw error;
    }
  }

  // Initialize Google One Tap (optional - for seamless sign-in)
  initializeOneTap(callback) {
    if (!window.google || !this.clientId) return;

    window.google.accounts.id.initialize({
      client_id: this.clientId,
      callback: callback,
      auto_select: false,
      cancel_on_tap_outside: true,
    });
  }

  // Show Google One Tap prompt
  showOneTap() {
    if (!window.google) return;
    
    window.google.accounts.id.prompt((notification) => {
      // Handle notification if needed
    });
  }

  // Render Google Sign-In button
  renderSignInButton(elementId, options = {}) {
    if (!window.google || !this.clientId) return;

    const defaultOptions = {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
      logo_alignment: 'left',
      width: '100%',
    };

    window.google.accounts.id.renderButton(
      document.getElementById(elementId),
      { ...defaultOptions, ...options }
    );
  }
}

export default new GoogleAuthService();
